import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { clearAuth } from '../utils/authUtil';
import { usePopup } from '../context/PopupContext';
import { readinessLevelColor, readinessLevelLabel } from '../components/diagnostic/diagnosticHelpers';
import { SkeletonCard } from '../components/ui/Skeleton';
import Icon from '../components/ui/Icon';
import Avatar from '../components/ui/Avatar';
import ErrorState from '../components/ui/ErrorState';
import LevelBadge from '../components/player/LevelBadge';
import XPBar from '../components/player/XPBar';
import Streak from '../components/player/Streak';
import AchievementCard from '../components/player/AchievementCard';
import SkillMap from '../components/player/SkillMap';
import PersonalRecordsGrid from '../components/player/PersonalRecordsGrid';
import AvatarPickerSheet from '../components/student/AvatarPickerSheet';
import { useStudent } from '../components/student/StudentContext';
import './StudentProfilePage.css';

// Player Profile — "this is my Zaptude character". Identity first; then Achievements, Skills, Personal bests and
// Readiness (all real evidence); then a clearly SEPARATE Account section (settings, password, log out).
// Reached from the avatar in the app bar (there is no Profile tab).
const StudentProfilePage = () => {
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const { player, setAvatarId } = useStudent();
  const [data, setData] = useState(null);
  const [progression, setProgression] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(null);
  const [pickAvatar, setPickAvatar] = useState(false);

  const load = () => {
    setLoading(true); setFailed(null);
    Promise.allSettled([
      API.get('/portal/dashboard'),
      API.get('/portal/progression'),
      API.get('/portal/diagnostic-intelligence'),
    ]).then(([dashRes, progRes, diagRes]) => {
      if (dashRes.status === 'fulfilled') setData(dashRes.value.data); else setFailed(dashRes.reason);
      if (progRes.status === 'fulfilled') setProgression(progRes.value.data);
      if (diagRes.status === 'fulfilled') setDiagnostic(diagRes.value.data);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const handleLogout = async () => {
    const confirmed = await showConfirm('Log out', 'Are you sure you want to log out of Zaptude?');
    if (confirmed) { clearAuth(); navigate('/login'); }
  };

  if (loading) {
    return <div className="z-page z-page--narrow"><SkeletonCard lines={3} /><SkeletonCard lines={4} /></div>;
  }
  if (failed || !data?.student) return <div className="z-page z-page--narrow"><ErrorState error={failed} onRetry={load} /></div>;

  const { student, overallStats, readiness } = data;

  return (
    <div className="z-page z-page--narrow profile-page">
      {/* ── Identity ── */}
      <section className="profile-hero" aria-label="Your player identity">
        <button type="button" className="profile-avatar-btn" onClick={() => setPickAvatar(true)} aria-label="Change your avatar">
          <Avatar avatarId={player?.avatar_id} name={student.name} size="xl" ring />
          <span className="profile-avatar-edit"><Icon name="edit" size={14} /> Change</span>
        </button>
        <h2 className="profile-name">{student.name}</h2>
        {player ? (
          <div className="profile-hero-stats">
            <div className="profile-hero-line"><LevelBadge level={player.level} size="md" /><Streak streak={player.streak} compact /></div>
            <XPBar levelProgress={player.levelProgress} />
          </div>
        ) : (
          <div className="profile-hero-skel" aria-hidden="true" />
        )}
      </section>

      {progression && (
        <section className="z-section" aria-labelledby="pf-ach">
          <h2 className="z-section-title" id="pf-ach">Achievements</h2>
          <div className="profile-achievement-grid">
            {progression.achievements.map(a => <AchievementCard key={a.id} achievement={a} />)}
          </div>
        </section>
      )}

      {diagnostic && (
        <section className="z-section" aria-labelledby="pf-skills">
          <h2 className="z-section-title" id="pf-skills">Skills</h2>
          <SkillMap topics={diagnostic.topics || []} />
        </section>
      )}

      {progression && (
        <section className="z-section" aria-labelledby="pf-pb">
          <h2 className="z-section-title" id="pf-pb">Personal bests</h2>
          <PersonalRecordsGrid records={progression.personalRecords} />
        </section>
      )}

      <section className="z-section" aria-labelledby="pf-ready">
        <h2 className="z-section-title" id="pf-ready">Placement readiness</h2>
        <div className="z-surface profile-ready">
          <Icon name="readiness" size={28} className="profile-ready-icon" />
          {readiness?.available ? (
            <div>
              <div className="profile-ready-level" style={{ color: readinessLevelColor(readiness.level) }}>{readinessLevelLabel(readiness.level)}</div>
              <div className="profile-ready-sub">Score {readiness.score != null ? `${parseFloat(readiness.score).toFixed(1)}%` : '—'} · {readiness.confidence} confidence</div>
            </div>
          ) : (
            <div className="profile-ready-sub">Complete a few challenges to discover your readiness.</div>
          )}
        </div>
      </section>

      {/* ── Account: deliberately separate from identity ── */}
      <div className="profile-divider" role="separator" />
      <section className="z-section" aria-labelledby="pf-account">
        <h2 className="z-section-title" id="pf-account">Account</h2>
        <div className="z-list">
          <div className="z-list-row profile-static"><span className="z-list-row-body"><span className="z-list-row-sub">Roll number</span><span className="z-list-row-title">{student.roll_number || '—'}</span></span></div>
          <div className="z-list-row profile-static"><span className="z-list-row-body"><span className="z-list-row-sub">Branch</span><span className="z-list-row-title">{student.branch || '—'}</span></span></div>
          <div className="z-list-row profile-static"><span className="z-list-row-body"><span className="z-list-row-sub">CGPA · Arrears</span><span className="z-list-row-title">{student.cgpa || '—'} · {student.arrears ?? 0}</span></span></div>
          <div className="z-list-row profile-static"><span className="z-list-row-body"><span className="z-list-row-sub">Placement batch</span><span className="z-list-row-title">{student.placement_batch_year || '—'}</span></span></div>
          <div className="z-list-row profile-static"><span className="z-list-row-body"><span className="z-list-row-sub">Email</span><span className="z-list-row-title profile-wrap">{student.email}</span></span></div>
        </div>
        {overallStats?.tests_taken > 0 && (
          <p className="profile-footnote">{overallStats.tests_taken} assessment{overallStats.tests_taken === 1 ? '' : 's'} taken · {parseFloat(overallStats.avg_score || 0).toFixed(0)}% average score</p>
        )}
        <div className="z-list">
          <button type="button" className="z-list-row" onClick={() => navigate('/student/settings')}>
            <Icon name="settings" size={20} className="profile-row-icon" />
            <span className="z-list-row-body"><span className="z-list-row-title">Settings & password</span></span>
            <Icon name="chevron-right" size={18} />
          </button>
          <button type="button" className="z-list-row z-list-row--danger" onClick={handleLogout}>
            <Icon name="logout" size={20} />
            <span className="z-list-row-body"><span className="z-list-row-title">Log out</span></span>
          </button>
        </div>
      </section>

      {pickAvatar && (
        <AvatarPickerSheet
          currentAvatarId={player?.avatar_id}
          name={student.name}
          onClose={() => setPickAvatar(false)}
          onSaved={(id) => { setAvatarId(id); setPickAvatar(false); }}
        />
      )}
    </div>
  );
};

export default StudentProfilePage;
